import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Plus, Star, Trash2, UploadCloud, X, EyeOff, Eye, ArrowLeft, ArrowRight, RefreshCw, ArrowUpDown, Search } from "lucide-react";
import { CATEGORIES, STATUS_LABELS, FUEL_TYPES, categoryById } from "@/data/categories";
import { API, photoSrc, invalidateCategoryCache } from "@/lib/api";

const inputCls =
  "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#D4AF37]/60 transition-colors text-sm";

const EMPTY = { category_id: CATEGORIES[0].id, internal_name: "", public_title: "", year: 2020, price: "", sale_enabled: false, sale_price: "", deposit: "", fuel_type: "ჰიბრიდი", description: "", conditions: "", status: "available", plate: "", show_plate: false };

const QUICK_STATUS_STYLES = {
  available: "border-green-500/40 text-green-400",
  booked: "border-amber-500/40 text-amber-400",
  rented: "border-sky-500/40 text-sky-400",
  service: "border-red-500/40 text-red-400",
};

const STATUS_RANK = { available: 0, booked: 1, rented: 2, service: 3 };

const SORT_FIELDS = [
  { key: "fleet", label: "ფლოტის ID" },
  { key: "category", label: "კატეგორია" },
  { key: "year", label: "წელი" },
  { key: "price", label: "ფასი" },
  { key: "status", label: "სტატუსი" },
  { key: "created_at", label: "დამატების თარიღი" },
  { key: "updated_at", label: "ბოლო განახლება" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ka-GE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
};

function VehicleForm({ initial, onSave, onCancel, saving, testid }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <form
      data-testid={testid}
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          ...form,
          year: Number(form.year),
          price: Number(form.price),
          deposit: Number(form.deposit),
          sale_price: form.sale_enabled && form.sale_price !== "" ? Number(form.sale_price) : null,
        });
      }}
      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
    >
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">შინაური სახელი / ფლოტის ID <span className="text-[#D4AF37]">*</span></label>
        <input data-testid={`${testid}-internal-name`} type="text" required value={form.internal_name} onChange={set("internal_name")} className={inputCls} placeholder="მაგ. Prius #P-021" />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">საჯარო სათაური (სურვილისამებრ)</label>
        <input data-testid={`${testid}-public-title`} type="text" value={form.public_title} onChange={set("public_title")} className={inputCls} placeholder="მაგ. თეთრი Prius — პანორამული სახურავი" />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">კატეგორია</label>
        <select data-testid={`${testid}-category`} value={form.category_id} onChange={set("category_id")} className={inputCls}>
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id} className="bg-[#141721]">{c.name} {c.years}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">გამოშვების წელი</label>
        <input data-testid={`${testid}-year`} type="number" required min="1990" max="2030" value={form.year} onChange={set("year")} className={inputCls} />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">ძირითადი ფასი (₾/დღე)</label>
        <input data-testid={`${testid}-price`} type="number" required min="1" value={form.price} onChange={set("price")} className={inputCls} placeholder="80" />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">დეპოზიტი (₾)</label>
        <input data-testid={`${testid}-deposit`} type="number" required min="0" value={form.deposit} onChange={set("deposit")} className={inputCls} placeholder="200" />
      </div>
      <div className="flex items-end pb-2">
        <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" data-testid={`${testid}-sale-toggle`} checked={!!form.sale_enabled} onChange={set("sale_enabled")} className="w-4 h-4 accent-[#D4AF37]" />
          ფასდაკლება აქტიურია
        </label>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Sale ფასი (₾/დღე)</label>
        <input
          data-testid={`${testid}-sale-price`}
          type="number"
          min="1"
          disabled={!form.sale_enabled}
          value={form.sale_price ?? ""}
          onChange={set("sale_price")}
          className={`${inputCls} disabled:opacity-40`}
          placeholder="70"
        />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">საწვავის ტიპი</label>
        <select data-testid={`${testid}-fuel`} value={form.fuel_type} onChange={set("fuel_type")} className={inputCls}>
          {FUEL_TYPES.map((f) => (
            <option key={f} value={f} className="bg-[#141721]">{f}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">სტატუსი</label>
        <select data-testid={`${testid}-status`} value={form.status} onChange={set("status")} className={inputCls}>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k} className="bg-[#141721]">{v}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">სახელმწიფო ნომერი (მხოლოდ ადმინისთვის)</label>
        <input data-testid={`${testid}-plate`} type="text" value={form.plate} onChange={set("plate")} className={inputCls} placeholder="AB-123-CD" />
      </div>
      <div className="flex items-end pb-2">
        <label className="flex items-center gap-2.5 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" data-testid={`${testid}-show-plate`} checked={form.show_plate} onChange={set("show_plate")} className="w-4 h-4 accent-[#D4AF37]" />
          ნომრის ჩვენება საჯაროდ
        </label>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs text-gray-400 mb-1.5">მოკლე აღწერა</label>
        <textarea data-testid={`${testid}-description`} rows={3} value={form.description} onChange={set("description")} className={`${inputCls} resize-none`} placeholder="მაგ. გარბენი, მდგომარეობა, დამატებითი დეტალები..." />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs text-gray-400 mb-1.5">ქირის პირობები (თითო ხაზზე თითო პირობა — ცარიელისას გამოიყენება კატეგორიის პირობები)</label>
        <textarea data-testid={`${testid}-conditions`} rows={4} value={form.conditions} onChange={set("conditions")} className={`${inputCls} resize-none`} placeholder={"სრული დაზღვევა\nუფასო კვირა დღეები\n..."} />
      </div>
      <div className="sm:col-span-2 flex gap-3">
        <button type="submit" disabled={saving} data-testid={`${testid}-save`} className="btn-gold !py-2.5 !px-6 text-sm disabled:opacity-60">
          {saving ? "ინახება..." : "შენახვა"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} data-testid={`${testid}-cancel`} className="btn-ghost !py-2.5 !px-6 text-sm">
            <X className="w-4 h-4" /> გაუქმება
          </button>
        )}
      </div>
    </form>
  );
}

function PhotoManager({ vehicle, auth, onChange }) {
  const inputRef = useRef(null);
  const replaceRefs = useRef({});
  const [busy, setBusy] = useState(false);
  const photos = vehicle.photos || [];

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      await axios.post(`${API}/vehicles/${vehicle.id}/photos`, fd, auth);
      toast.success("ფოტო(ები) აიტვირთა");
      onChange();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "ატვირთვა ვერ მოხერხდა");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChange();
    } catch {
      toast.error("ოპერაცია ვერ მოხერხდა");
    }
  };

  const move = async (index, dir) => {
    const ids = photos.map((p) => p.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await act(() => axios.put(`${API}/vehicles/${vehicle.id}/photos/order`, { photo_ids: ids }, auth), "რიგი შეიცვალა");
  };

  const replace = async (photoId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await axios.post(`${API}/photos/${photoId}/replace`, fd, auth);
      toast.success("ფოტო ჩანაცვლდა");
      onChange();
    } catch {
      toast.error("ჩანაცვლება ვერ მოხერხდა");
    } finally {
      setBusy(false);
      if (replaceRefs.current[photoId]) replaceRefs.current[photoId].value = "";
    }
  };

  return (
    <div data-testid={`admin-photos-${vehicle.id}`} className="mt-2 pt-4 border-t border-white/10">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">ფოტოები: {photos.length} — პირველი (★) არის მთავარი</p>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`admin-upload-btn-${vehicle.id}`}
          className="btn-ghost !py-2 !px-4 text-xs"
        >
          <UploadCloud className="w-4 h-4" /> {busy ? "იტვირთება..." : "ფოტოების დამატება"}
        </button>
        <input type="file" accept="image/*" multiple hidden ref={inputRef} data-testid={`admin-file-input-${vehicle.id}`} onChange={(e) => upload(e.target.files)} />
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p, i) => (
            <div key={p.id} className={`relative rounded-lg overflow-hidden border group ${p.is_cover ? "border-[#D4AF37]" : "border-white/15"}`} data-testid={`admin-photo-${p.id}`}>
              <img src={photoSrc(p)} alt="" className="w-full h-24 object-cover" />
              {p.is_cover && (
                <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#D4AF37] text-[#0A0B0E]">მთავარი</span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between p-1.5 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex gap-1">
                  <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`admin-photo-left-${p.id}`} className="w-7 h-7 rounded-md bg-white/15 text-white flex items-center justify-center disabled:opacity-30">
                    <ArrowLeft className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === photos.length - 1} data-testid={`admin-photo-right-${p.id}`} className="w-7 h-7 rounded-md bg-white/15 text-white flex items-center justify-center disabled:opacity-30">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex gap-1">
                  {!p.is_cover && (
                    <button
                      onClick={() => act(() => axios.put(`${API}/photos/${p.id}/cover`, {}, auth), "მთავარი ფოტო შეიცვალა")}
                      data-testid={`admin-cover-btn-${p.id}`}
                      className="w-7 h-7 rounded-md bg-[#D4AF37] text-[#0A0B0E] flex items-center justify-center"
                    >
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => replaceRefs.current[p.id]?.click()} data-testid={`admin-replace-btn-${p.id}`} className="w-7 h-7 rounded-md bg-white/15 text-white flex items-center justify-center">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                  <input type="file" accept="image/*" hidden ref={(el) => (replaceRefs.current[p.id] = el)} onChange={(e) => replace(p.id, e.target.files[0])} />
                  <button
                    onClick={() => act(() => axios.delete(`${API}/photos/${p.id}`, auth), "ფოტო წაიშალა")}
                    data-testid={`admin-delete-btn-${p.id}`}
                    className="w-7 h-7 rounded-md bg-red-500/90 text-white flex items-center justify-center"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminVehicles({ auth }) {
  const [vehicles, setVehicles] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ category: "all", year: "all", status: "all", availability: "all", sale: "all" });
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("asc");
  const [appliedSort, setAppliedSort] = useState(null);

  const load = () => {
    axios.get(`${API}/admin/vehicles`, auth).then((r) => setVehicles(r.data)).catch(() => {});
    invalidateCategoryCache();
  };

  useEffect(load, []);

  const create = async (form) => {
    setSaving(true);
    try {
      await axios.post(`${API}/vehicles`, form, auth);
      toast.success("ავტომობილი დაემატა — გახსენი რედაქტირება და დაამატე ფოტოები");
      setAdding(false);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "დამატება ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const update = async (id, form) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/vehicles/${id}`, form, auth);
      // ელემენტის ლოკალური განახლება — სია ადგილს არ იცვლის
      setVehicles((prev) => prev.map((v) => (v.id === id ? { ...v, ...r.data } : v)));
      toast.success("ცვლილებები შენახულია");
      setEditingId(null);
    } catch {
      toast.error("შენახვა ვერ მოხერხდა");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("ნამდვილად გსურთ ავტომობილის წაშლა? მისი ფოტოებიც წაიშლება.")) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`, auth);
      toast.success("ავტომობილი წაიშალა");
      setVehicles((prev) => prev.filter((v) => v.id !== id));
    } catch {
      toast.error("წაშლა ვერ მოხერხდა");
    }
  };

  const quick = async (id, path, body, msg) => {
    try {
      await axios.put(`${API}/vehicles/${id}/${path}`, body, auth);
      toast.success(msg);
      setVehicles((prev) => prev.map((v) => (v.id === id ? { ...v, ...body } : v)));
    } catch {
      toast.error("ოპერაცია ვერ მოხერხდა");
    }
  };

  const years = useMemo(() => [...new Set(vehicles.map((v) => v.year))].sort((a, b) => a - b), [vehicles]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return vehicles.filter((v) => {
      if (q) {
        const cat = categoryById(v.category_id);
        const hay = [v.internal_name, v.public_title, String(v.year || ""), cat?.name, cat?.years, v.plate]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.category !== "all" && v.category_id !== filters.category) return false;
      if (filters.year !== "all" && v.year !== Number(filters.year)) return false;
      if (filters.status !== "all" && v.status !== filters.status) return false;
      if (filters.availability === "available" && v.status !== "available") return false;
      if (filters.availability === "unavailable" && v.status === "available") return false;
      if (filters.sale === "active" && !(v.sale_enabled && v.sale_price)) return false;
      if (filters.sale === "inactive" && v.sale_enabled && v.sale_price) return false;
      return true;
    });
  }, [vehicles, filters, query]);

  const list = useMemo(() => {
    if (!appliedSort) return filtered;
    const dir = appliedSort.dir === "desc" ? -1 : 1;
    const val = (v) => {
      switch (appliedSort.key) {
        case "fleet": return (v.internal_name || "").toLowerCase();
        case "category": return (categoryById(v.category_id)?.name || "").toLowerCase();
        case "year": return v.year || 0;
        case "price": return v.price || 0;
        case "status": return STATUS_RANK[v.status] ?? 9;
        case "updated_at": return v.updated_at || "";
        default: return v.created_at || "";
      }
    };
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "ka");
      return (cmp || String(a.created_at).localeCompare(String(b.created_at)) || String(a.id).localeCompare(String(b.id))) * dir;
    });
  }, [filtered, appliedSort]);

  const applySort = () => {
    setAppliedSort({ key: sortKey, dir: sortDir });
    toast.success("სია დალაგდა");
  };

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-vehicles-section">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">
          ავტომობილები ({vehicles.length})
          {filtered.length !== vehicles.length && <span className="text-sm text-gray-400 font-normal ml-2">· ნაჩვენებია {filtered.length}</span>}
        </h2>
        <button onClick={() => setAdding(!adding)} data-testid="admin-add-vehicle-btn" className="btn-gold !py-2.5 !px-5 text-sm">
          {adding ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {adding ? "დახურვა" : "ახალი ავტომობილი"}
        </button>
      </div>

      {adding && (
        <div className="card-lux p-6 mb-8">
          <VehicleForm initial={EMPTY} onSave={create} saving={saving} testid="admin-new-vehicle" />
        </div>
      )}

      {vehicles.length > 0 && (
        <div className="card-lux p-5 mb-6 space-y-4" data-testid="admin-fleet-controls">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              data-testid="admin-vehicle-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className={`${inputCls} !pl-11`}
              placeholder="ძებნა: ფლოტის ID, სახელი, ნომერი, წელი, კატეგორია... (მაგ. 021, 2020, Model Y)"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                data-testid="admin-vehicle-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-1"
                aria-label="გასუფთავება"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">კატეგორია</label>
              <select data-testid="admin-filter-category" value={filters.category} onChange={setFilter("category")} className={inputCls}>
                <option value="all" className="bg-[#141721]">ყველა</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#141721]">{c.name} {c.years}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">წელი</label>
              <select data-testid="admin-filter-year" value={filters.year} onChange={setFilter("year")} className={inputCls}>
                <option value="all" className="bg-[#141721]">ყველა</option>
                {years.map((y) => (
                  <option key={y} value={y} className="bg-[#141721]">{y}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">სტატუსი</label>
              <select data-testid="admin-filter-status" value={filters.status} onChange={setFilter("status")} className={inputCls}>
                <option value="all" className="bg-[#141721]">ყველა</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#141721]">{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">ხელმისაწვდომობა</label>
              <select data-testid="admin-filter-availability" value={filters.availability} onChange={setFilter("availability")} className={inputCls}>
                <option value="all" className="bg-[#141721]">ყველა</option>
                <option value="available" className="bg-[#141721]">თავისუფალი</option>
                <option value="unavailable" className="bg-[#141721]">დაკავებული</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">ფასდაკლება</label>
              <select data-testid="admin-filter-sale" value={filters.sale} onChange={setFilter("sale")} className={inputCls}>
                <option value="all" className="bg-[#141721]">ყველა</option>
                <option value="active" className="bg-[#141721]">აქტიური</option>
                <option value="inactive" className="bg-[#141721]">არააქტიური</option>
              </select>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-white/10">
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">დალაგება ველით</label>
              <select data-testid="admin-sort-key" value={sortKey} onChange={(e) => setSortKey(e.target.value)} className={inputCls}>
                {SORT_FIELDS.map((f) => (
                  <option key={f.key} value={f.key} className="bg-[#141721]">{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] text-gray-500 mb-1">მიმართულება</label>
              <select data-testid="admin-sort-dir" value={sortDir} onChange={(e) => setSortDir(e.target.value)} className={inputCls}>
                <option value="asc" className="bg-[#141721]">ზრდადი</option>
                <option value="desc" className="bg-[#141721]">კლებადი</option>
              </select>
            </div>
            <button onClick={applySort} data-testid="admin-sort-apply" className="btn-gold !py-2.5 !px-6 text-sm">
              <ArrowUpDown className="w-4 h-4" /> დალაგება
            </button>
            {appliedSort && (
              <button onClick={() => setAppliedSort(null)} data-testid="admin-sort-reset" className="btn-ghost !py-2.5 !px-4 text-xs">
                საწყისი რიგი
              </button>
            )}
            {appliedSort && (
              <span className="text-xs text-[#E5C378]" data-testid="admin-sort-active">
                {SORT_FIELDS.find((f) => f.key === appliedSort.key)?.label} · {appliedSort.dir === "asc" ? "ზრდადი" : "კლებადი"}
              </span>
            )}
          </div>
        </div>
      )}

      {vehicles.length === 0 && !adding ? (
        <div className="border border-dashed border-white/20 rounded-xl p-12 text-center text-gray-500" data-testid="admin-vehicles-empty">
          ავტომობილები ჯერ არ არის — დაამატე პირველი
        </div>
      ) : (
        <div className="card-lux overflow-hidden" data-testid="admin-vehicles-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="px-4 py-3 font-medium">ფოტო</th>
                  <th className="px-4 py-3 font-medium">ფლოტის ID</th>
                  <th className="px-4 py-3 font-medium">კატეგორია</th>
                  <th className="px-4 py-3 font-medium">წელი</th>
                  <th className="px-4 py-3 font-medium">ფასი</th>
                  <th className="px-4 py-3 font-medium">Sale</th>
                  <th className="px-4 py-3 font-medium">სტატუსი</th>
                  <th className="px-4 py-3 font-medium">ხელმისაწვდ.</th>
                  <th className="px-4 py-3 font-medium">განახლდა</th>
                  <th className="px-4 py-3 font-medium text-right">მოქმედება</th>
                </tr>
              </thead>
              <tbody>
                {list.map((v) => {
                  const cat = categoryById(v.category_id);
                  const cover = (v.photos || [])[0];
                  const isAvailable = v.status === "available";
                  const expanded = editingId === v.id;
                  return [
                    <tr key={v.id} className={`border-b border-white/5 transition-colors ${expanded ? "bg-[#D4AF37]/5" : "hover:bg-white/[0.03]"}`} data-testid={`admin-vehicle-${v.id}`}>
                      <td className="px-4 py-3">
                        {cover ? (
                          <img src={photoSrc(cover)} alt="" className="w-16 h-11 rounded-lg object-cover border border-white/10" />
                        ) : (
                          <div className="w-16 h-11 rounded-lg bg-white/5 border border-dashed border-white/15" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{v.internal_name || "—"}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {v.waitlist_count > 0 && (
                            <a href={`/admin?tab=waitlist&vehicle=${v.id}`} data-testid={`admin-waitlist-link-${v.id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/40">
                              რიგში: {v.waitlist_count}
                            </a>
                          )}
                          {v.hidden && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/10 text-gray-400 border border-white/20" data-testid={`admin-hidden-badge-${v.id}`}>დამალული</span>}
                          {v.featured && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#D4AF37]/15 text-[#E5C378] border border-[#D4AF37]/40">მთავარ გვერდზე</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{cat?.name} {cat?.years}</td>
                      <td className="px-4 py-3 text-gray-300">{v.year}</td>
                      <td className="px-4 py-3 text-white whitespace-nowrap">{v.price} ₾</td>
                      <td className="px-4 py-3 whitespace-nowrap" data-testid={`admin-sale-cell-${v.id}`}>
                        {v.sale_enabled && v.sale_price ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-[#E5C378] font-semibold">{v.sale_price} ₾</span>
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#D4AF37] text-[#0A0B0E]">SALE</span>
                          </span>
                        ) : (
                          <span className="text-gray-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${QUICK_STATUS_STYLES[v.status]} bg-white/5`} data-testid={`admin-status-badge-${v.id}`}>
                          {STATUS_LABELS[v.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium ${isAvailable ? "text-green-400" : "text-gray-500"}`}>
                          {isAvailable ? "თავისუფალი" : "დაკავებული"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(v.updated_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditingId(expanded ? null : v.id)}
                            data-testid={`admin-edit-btn-${v.id}`}
                            className="btn-ghost !py-2 !px-3.5 text-xs"
                          >
                            <Pencil className="w-3.5 h-3.5" /> {expanded ? "დახურვა" : "რედაქტირება"}
                          </button>
                          <button
                            onClick={() => remove(v.id)}
                            data-testid={`admin-delete-vehicle-${v.id}`}
                            className="!py-2 !px-3.5 text-xs inline-flex items-center gap-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> წაშლა
                          </button>
                        </div>
                      </td>
                    </tr>,
                    expanded && (
                      <tr key={`${v.id}-edit`} className="border-b border-white/10 bg-white/[0.02]">
                        <td colSpan={10} className="px-5 py-5">
                          <div className="flex flex-wrap gap-2 mb-5" data-testid={`admin-quick-status-${v.id}`}>
                            {Object.entries(STATUS_LABELS).map(([k, label]) => (
                              <button
                                key={k}
                                onClick={() => v.status !== k && quick(v.id, "status", { status: k }, `სტატუსი: ${label}`)}
                                data-testid={`admin-status-${k}-${v.id}`}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                                  v.status === k
                                    ? "bg-[#D4AF37] text-[#0A0B0E] border-[#D4AF37]"
                                    : `bg-white/5 ${QUICK_STATUS_STYLES[k]} hover:bg-white/10`
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                            <button
                              onClick={() => quick(v.id, "featured", { featured: !v.featured }, v.featured ? "მოიხსნა მთავარი გვერდიდან" : "დაემატა მთავარ გვერდს")}
                              data-testid={`admin-featured-btn-${v.id}`}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors inline-flex items-center gap-1.5 ${v.featured ? "bg-[#D4AF37]/15 text-[#E5C378] border-[#D4AF37]/40" : "bg-white/5 text-gray-300 border-white/15 hover:border-[#D4AF37]/40"}`}
                            >
                              <Star className="w-3.5 h-3.5" /> {v.featured ? "მთავარიდან მოხსნა" : "მთავარ გვერდზე"}
                            </button>
                            <button
                              onClick={() => quick(v.id, "hide", { hidden: !v.hidden }, v.hidden ? "ავტომობილი გამოჩნდა" : "ავტომობილი დაიმალა")}
                              data-testid={`admin-hide-btn-${v.id}`}
                              className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white/5 text-gray-300 border-white/15 hover:border-[#D4AF37]/40 transition-colors inline-flex items-center gap-1.5"
                            >
                              {v.hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} {v.hidden ? "გამოჩენა" : "დამალვა"}
                            </button>
                          </div>
                          <div className="mb-2 p-5 rounded-xl bg-white/5 border border-white/10">
                            <VehicleForm
                              initial={{ category_id: v.category_id, internal_name: v.internal_name || "", public_title: v.public_title || "", year: v.year, price: v.price, sale_enabled: !!v.sale_enabled, sale_price: v.sale_price ?? "", deposit: v.deposit, fuel_type: v.fuel_type || "ჰიბრიდი", description: v.description, conditions: v.conditions || "", status: v.status, plate: v.plate || "", show_plate: !!v.show_plate }}
                              onSave={(form) => update(v.id, form)}
                              onCancel={() => setEditingId(null)}
                              saving={saving}
                              testid={`admin-edit-form-${v.id}`}
                            />
                          </div>
                          <PhotoManager vehicle={v} auth={auth} onChange={load} />
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
          {list.length === 0 && (
            <div className="p-10 text-center text-gray-500 text-sm" data-testid="admin-vehicles-no-results">
              ფილტრებით ვერაფერი მოიძებნა
            </div>
          )}
        </div>
      )}
    </div>
  );
}
